import C from "@/components/button/WButton.module.css";

import { WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import type { BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { copyElementToClipboard, copyTextToClipboard } from "@/utils/clipboard.ts";
import { useLingui } from "@lingui/react/macro";
import { discardPromise, discardPromiseFn } from "@warrenio/utils/promise/discardPromise";
import sleep from "@warrenio/utils/promise/sleep";
import { useRef, useState, type ReactNode } from "react";

interface ClipBoardTooltipBaseProps {
    children?: ReactNode | string;
    /** Copy rich HTML snippet of the children to the clipboard */
    isHtml?: boolean;
    text?: string;
    props?: BaseProps;
    hideIcon?: boolean;
}

export interface ClipBoardTooltipPlainProps extends ClipBoardTooltipBaseProps {
    isHtml?: false | undefined;
    text: string;
}

export interface ClipBoardTooltipSimpleProps extends ClipBoardTooltipBaseProps {
    /** If there is a single child which is a string, there is no need to specify other props. */
    children: string | number;
}

export interface ClipboardTooltipHtmlProps extends ClipBoardTooltipBaseProps {
    isHtml: true;
    /** Optional property to specify fallback text for clipboard. Not required if `isHtml={true}`. */
    text?: string;
}

export type ClipboardTooltipProps =
    | ClipBoardTooltipPlainProps
    | ClipboardTooltipHtmlProps
    | ClipBoardTooltipSimpleProps;

export function ClipBoardTooltip({
    children,
    text = typeof children === "string" || typeof children === "number" ? String(children) : undefined,
    isHtml = false,
    hideIcon,
    props,
}: ClipboardTooltipProps) {
    const { t } = useLingui();
    const childrenRef = useRef<HTMLElement>(null);

    const [isCopied, setIsCopied] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isForceOpen, setIsForceOpen] = useState(false);

    const actualIsOpen = isForceOpen || isOpen;

    const handleCopyClick = async () => {
        if (isHtml) {
            await copyElementToClipboard(childrenRef.current!, text);
        } else {
            await copyTextToClipboard(text!);
        }
        setIsCopied(true);
        setIsForceOpen(true);

        await sleep(1000);
        setIsCopied(false);

        await sleep(1000);
        setIsForceOpen(false);
    };

    const icon = !hideIcon && <MaskIcon className="jp-duplicate-icon size-0.875rem min-w-0.875rem text-primary ml-1" />;

    function isUuid(value: string) {
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
    }

    return (
        <WTooltip text={isCopied ? t`Copied!` : t`Copy to clipboard`} isOpen={actualIsOpen} onOpenChange={setIsOpen}>
            {isHtml ? (
                // TODO: This is not accessible with keyboard navigation
                <span
                    className={cn(C.Button, C.xs, C.ghost, C.primary, "cursor-pointer")}
                    onClick={(e) => {
                        e.stopPropagation();
                        discardPromise(handleCopyClick());
                    }}
                    {...props}
                >
                    <span className="color-text" ref={childrenRef}>
                        {children}
                    </span>
                    {icon}
                </span>
            ) : (
                <WButton
                    variant="ghost"
                    color="primary"
                    size="xs"
                    action={discardPromiseFn(handleCopyClick)}
                    {...props}
                >
                    <span
                        className={cn(
                            "color-text",
                            isUuid(children as string) && "max-w-9rem text-ellipsis overflow-x-hidden",
                        )}
                    >
                        {children}
                    </span>
                    {icon}
                </WButton>
            )}
        </WTooltip>
    );
}
